import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getProduits,
  getProduitById,
  createProduit,
  updateProduit,
  deleteProduit,
} from '@/services/produits'
import type { ProduitInsert, ProduitUpdate } from '@/types/database-real'
import type { ProduitFilters } from '@/types/produit'
import toast from 'react-hot-toast'

// ============================================
// GET ALL PRODUITS
// ============================================
export function useProduits(filters?: ProduitFilters) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['produits', filters],
    queryFn: () => getProduits(filters),
  })

  return {
    produits: data?.data || [],
    total: data?.count || 0,
    isLoading,
    error,
    refetch,
  }
}

// ============================================
// GET PRODUIT BY ID
// ============================================
export function useProduit(id: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['produit', id],
    queryFn: () => getProduitById(id),
    enabled: !!id,
  })

  return {
    produit: data,
    isLoading,
    error,
  }
}

// ============================================
// CREATE PRODUIT
// ============================================
export function useCreateProduit() {
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState(false)

  const mutation = useMutation({
    mutationFn: (produit: ProduitInsert) => createProduit(produit),
    onMutate: () => {
      setIsCreating(true)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produits'] })
      toast.success('Produit créé avec succès')
      setIsCreating(false)
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la création: ${error.message}`)
      setIsCreating(false)
    },
  })

  return {
    createProduit: mutation.mutateAsync,
    isCreating,
  }
}

// ============================================
// UPDATE PRODUIT
// ============================================
export function useUpdateProduit() {
  const queryClient = useQueryClient()
  const [isUpdating, setIsUpdating] = useState(false)

  const mutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProduitUpdate }) =>
      updateProduit(id, data),
    onMutate: () => {
      setIsUpdating(true)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['produits'] })
      queryClient.invalidateQueries({ queryKey: ['produit', variables.id] })
      toast.success('Produit mis à jour')
      setIsUpdating(false)
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la mise à jour: ${error.message}`)
      setIsUpdating(false)
    },
  })

  return {
    updateProduit: mutation.mutateAsync,
    isUpdating,
  }
}

// ============================================
// DELETE PRODUIT
// ============================================
export function useDeleteProduit() {
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)

  const mutation = useMutation({
    mutationFn: (id: string) => deleteProduit(id),
    onMutate: () => {
      setIsDeleting(true)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produits'] })
      toast.success('Produit supprimé')
      setIsDeleting(false)
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`)
      setIsDeleting(false)
    },
  })

  return {
    deleteProduit: mutation.mutateAsync,
    isDeleting,
  }
}
